import React, { useState, useEffect } from 'react';
import { FiArrowLeft, FiArrowRight } from 'react-icons/fi';
import { Progress } from '@/components/custom/progress';
import { ExamsQuestionsModalEnd } from './exams-questions-modal-end';
import { useMutation } from 'jsonapi-react';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { ExamsQuestionsClock } from './exams-questions-clock';
import { useTranslation } from 'react-i18next';
import { DashboardContent } from '@/layouts/dashboard';
import { PageCol, PageContent, PageSidebar } from '@/components/custom/page';
import { Widget, WidgetBody, WidgetCount, WidgetHeader, WidgetHeaderContent, WidgetTitle } from '@/components/custom/widget';
import { Button } from '@/components/ui/button';
import { QuestionCardAnswer, QuestionCardAnswerLetter, QuestionCardAnswerWrapper, QuestionCardIntroduction, QuestionCardNav, QuestionCardPrint, QuestionCardStatement, QuestionCardTextMotivator, QuestionCardTitle } from '@/components/custom/question';

export const ExamsQuestionsAlternative = props => {
  const { alternative, question, selectedAlternativeId, setSelectedAlternativeId } = props;
  return (
    <QuestionCardAnswer>
      <QuestionCardAnswerLetter>{alternative.name}</QuestionCardAnswerLetter>
      <input
        type="radio"
        name={`question-${question?.id}`}
        id={alternative.id}
        checked={selectedAlternativeId === alternative.id}
        onChange={() => {
          setSelectedAlternativeId(alternative.id);
        }}
      />
      <label
        htmlFor={`${alternative?.id}`}
        dangerouslySetInnerHTML={{ __html: alternative.description }}
      />
    </QuestionCardAnswer>
  );
};

export function ExamsQuestionsContent(props) {
  const { t } = useTranslation();
  const { selectQuestionBookTaxonomy, currentQuestion, setCurrentQuestion, questions, handleSelectedAnswersChange, examTime, questionBooksUser, updateLastAnswerSubmited, questionBook, taxonomies, activeGroupedQuestions } = props;

  const client = useClient();
  const [addAnswer] = useMutation('answers');
  const [selectedAlternativeId, setSelectedAlternativeId] = useState(null);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [question, setQuestion] = useState();
  const [activeAnswer, setActiveAnswer] = useState();
  const [elapsedTime, setElapsedTime] = useState(0);

  const submitAnswer = async alternativeId => {
    if (alternativeId !== null) {
      // try {
      let response;
      if (!activeAnswer) {
        response = await addAnswer({
          'alternative-id': alternativeId,
          'question-id': question.id,
          'question-books-user-id': questionBooksUser?.id
        });
      } else {
        response = await client.mutate(['answers', activeAnswer.id], {
          'alternative-id': alternativeId,
          'question-id': question.id,
          'question-books-user-id': questionBooksUser?.id
        });
      }

      if (response.error) {
        toast.error(response.error.title);
      } else {
        const newAnswer = {
          id: response.data.id,
          alternativeId: response.data['alternative-id'],
          questionId: response.data['question-id']
        };

        setActiveAnswer(newAnswer);
        updateLastAnswerSubmited(newAnswer);
      }
    } else {
      toast.error(t('toast.errorSelectAlternative'));
    }
  };

  const countCorrectAnswers = () => {
    return questionBooksUser.answers.reduce((accumulator, answer) => {
      accumulator += answer.is_correct ? 1 : 0;
      return accumulator;
    }, 0);
  };

  function formatDateTime(dateString) {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes();

    return `${hours}h${minutes}${t('exams.minOfDay')} ${day < 10 ? '0' + day : day}/${month < 10 ? '0' + month : month}/${year}`;
  }

  const formattedDate = formatDateTime(questionBook['finished-at']);

  const handleFinishExam = () => setShowModal(true);

  useEffect(() => {
    if (currentQuestion) {
      setQuestion(questions.find(q => q.id === currentQuestion));
    }
  }, [selectedAnswers, handleSelectedAnswersChange, currentQuestion]);

  useEffect(() => {
    const newActiveAnswer = taxonomies?.[activeGroupedQuestions]?.answers?.find(a => a?.questionId === question?.id);
    setActiveAnswer(newActiveAnswer);
    setSelectedAlternativeId(newActiveAnswer?.alternativeId);
  }, [question, taxonomies]);

  const prevQuestion = () => {
    const questionIndex = combinedArray.findIndex(qId => qId === currentQuestion) - 1;
    const taxonomyIndex = taxonomies.findIndex(t => t.questions.map(q => q.id).includes(combinedArray[questionIndex]));
    setCurrentQuestion(combinedArray[questionIndex]);
    selectQuestionBookTaxonomy(taxonomyIndex);
  };

  const handleNextQuestion = () => {
    const currentIndex = combinedArray.findIndex(qId => qId === question?.id);
    if (currentIndex <= combinedArray.length) {
      const taxonomyIndex = taxonomies.findIndex(t => t.questions.map(q => q.id).includes(combinedArray[currentIndex + 1]));
      setCurrentQuestion(combinedArray[currentIndex + 1]);
      selectQuestionBookTaxonomy(taxonomyIndex);
    }
    setSelectedAlternativeId(null);
  };

  const getCombinedArray = () => {
    const examQBT = questionBook?.['question-book-taxonomies'];
    if (!examQBT) {
      return [];
    }
    return examQBT.reduce((accumulator, taxonomy) => {
      return accumulator.concat(taxonomy.question_ids);
    }, []);
  };
  const combinedArray = getCombinedArray();

  function countAnswered() {
    return questionBook.taxonomies.reduce((accumulator, question) => {
      accumulator += question.answers.length;
      return accumulator;
    }, 0);
  }

  function sumQuestionIds(exam) {
    const taxonomyArray = exam['question-book-taxonomies']?.map(taxonomy => taxonomy.question_ids) ?? [];
    return taxonomyArray.reduce((accumulator, current) => accumulator + current.length, 0);
  }

  return (
    <DashboardContent viewMode="create">
      <PageContent>
        <PageCol bg>
          <div>
            <QuestionCardIntroduction>
              {t('exams.questionTitle')} {combinedArray.indexOf(currentQuestion) + 1}
            </QuestionCardIntroduction>

            {question?.['motivator-texts']?.map((motivatorText, index) => (
              <QuestionCardTextMotivator key={index}>
                <QuestionCardTitle dangerouslySetInnerHTML={{ __html: motivatorText.title }} />
                <div dangerouslySetInnerHTML={{ __html: motivatorText.body }} />
                <br />
                <QuestionCardPrint dangerouslySetInnerHTML={{ __html: motivatorText.print }} />
              </QuestionCardTextMotivator>
            ))}

            <QuestionCardStatement dangerouslySetInnerHTML={{ __html: question?.description }} />

            <QuestionCardAnswerWrapper>
              {question?.alternatives?.map(alternative => (
                <ExamsQuestionsAlternative
                  key={alternative.id}
                  submitAnswer={submitAnswer}
                  selectedAlternativeId={selectedAlternativeId}
                  setSelectedAlternativeId={setSelectedAlternativeId}
                  question={question}
                  alternative={alternative}
                  activeAnswer={activeAnswer}
                />
              ))}
            </QuestionCardAnswerWrapper>

            {currentQuestion === combinedArray[combinedArray.length - 1] ? (
              <>
                <Button
                  wide
                  type="button"
                  onClick={() => {
                    submitAnswer(selectedAlternativeId);
                    setTimeout(handleFinishExam, 1000);
                  }}
                >
                  {t('exams.replyAndFinish')}
                </Button>

                <QuestionCardNav>
                  {currentQuestion !== combinedArray[0] && (
                    <Button
                      variant="none"
                      wide
                      type="button"
                      onClick={prevQuestion}
                    >
                      <FiArrowLeft /> {t('exams.previous')}
                    </Button>
                  )}
                </QuestionCardNav>
              </>
            ) : (
              <>
                <Button
                  wide
                  type="button"
                  disabled={!selectedAlternativeId}
                  onClick={() => {
                    submitAnswer(selectedAlternativeId);
                    handleSelectedAnswersChange(selectedAnswers);
                    handleNextQuestion();
                  }}
                >
                  {t('exams.reply')}
                </Button>

                <QuestionCardNav>
                  {currentQuestion !== combinedArray[0] && (
                    <Button
                      variant="none"
                      wide
                      type="button"
                      onClick={prevQuestion}
                    >
                      <FiArrowLeft /> {t('exams.previous')}
                    </Button>
                  )}

                  {currentQuestion !== combinedArray[combinedArray.length - 1] && (
                    <Button
                      variant="none"
                      wide
                      type="button"
                      onClick={handleNextQuestion}
                    >
                      {t('exams.next')} <FiArrowRight />
                    </Button>
                  )}
                </QuestionCardNav>
              </>
            )}
          </div>
        </PageCol>

        <PageSidebar>
          {currentQuestion > 3 ? (
            <Button
              variant="outline"
              fullWidth
              type="button"
              className="mb-12"
              onClick={() => {
                if (currentQuestion > 3) {
                  // stopTimer();
                  setShowModal(true);
                }
              }}
            >
              {t('exams.sendExam')}
            </Button>
          ) : (
            ''
          )}

          <ExamsQuestionsClock
            examTime={examTime}
            questionBooksUser={questionBooksUser}
            questionBook={questionBook}
            setElapsedTime={setElapsedTime}
            elapsedTime={elapsedTime}
          />

          <Widget>
            <WidgetHeader>
              <WidgetHeaderContent>
                <WidgetTitle>{t('textsCommon.questions')}</WidgetTitle>
                <WidgetCount>
                  <span>
                    {countAnswered()}/{sumQuestionIds(questionBook)}
                  </span>
                </WidgetCount>
              </WidgetHeaderContent>
            </WidgetHeader>

            <WidgetBody>
              <Progress valueNow={Math.floor((countAnswered() / sumQuestionIds(questionBook)) * 100)} />
            </WidgetBody>
          </Widget>
        </PageSidebar>
      </PageContent>

      <ExamsQuestionsModalEnd
        questionBooksUser={questionBooksUser}
        countCorrectAnswers={countCorrectAnswers}
        elapsedTime={elapsedTime}
        countAnswered={countAnswered}
        show={showModal}
        onClose={() => {
          setShowModal(false);
        }}
        formattedDate={formattedDate}
        questionBook={questionBook}
        selectedAnswers={selectedAnswers}
        questions={questions}
      />
    </DashboardContent>
  );
}
